import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, DatePicker, Tabs, InputNumber, Progress, Row, Space, Statistic, Table, Typography, message, Spin, Tooltip, Badge, Modal } from "antd";
import { EditOutlined, HistoryOutlined } from "@ant-design/icons";
import moment from "moment";
import 'moment-timezone';
import { TZ } from "../../utils/tz";
import { buildPeriodRange } from "../../utils/period";
import { getKeplerRobMetrics, saveManagerPlans, saveTotalPlan, getPlans, putPlans, getKpi, saveManualFacts, getFactsHistory } from "./api";
import { getRequest } from "../../utils/requests";
import { changePageTitle } from "../../utils/title";
import { useDateRange } from "../../contexts/DateRangeContext";
import GlobalDateRangePicker from "../../components/GlobalDateRangePicker";
import { SipuniAutoSync, syncCalls } from "./utils/sipuniApi";
import CallsSyncManager from "./components/CallsSyncManager";
import { EditFactsModal, FactsHistoryModal, DailyReport, ConversionReport } from "./components";
import "./rob.css";
// plots gauge removed; use antd Progress dashboard
const { TabPane } = Tabs;

const { Title, Text } = Typography;
const { MonthPicker } = DatePicker;

// Feature flag: включить/выключить отображение индикаторов ручных правок
const MANUAL_FLAGS_ENABLED = false;

const percent = (fact, plan) => {
  if (!plan || plan === 0) return 0;
  const value = Math.round((fact / plan) * 100);
  return Math.max(0, Math.min(100, value));
};

// Удаление ведущих нулей при вводе чисел в InputNumber
const stripLeadingZerosParser = (raw) => {
  const str = (raw ?? "").toString().replace(/\s/g, "");
  const cleaned = str.replace(/^0+(?=\d)/, "");
  if (cleaned === "") return null;
  const num = Number(cleaned);
  return Number.isNaN(num) ? null : num;
};

const formatCurrency = (value, currency) => {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: currency || "KZT",
      maximumFractionDigits: 0,
    }).format(value || 0);
  } catch (e) {
    return `${value || 0} ${currency || "₸"}`;
  }
};

const formatNumber = (value) => {
  try {
    return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(Number(value || 0));
  } catch (e) {
    return String(value || 0);
  }
};

// Функция для определения цвета на основе процента выполнения
// до 49% - красный, 50-80% - желтый, 81-100% - зелёный, 101%+ - бирюзовый
const getCompletionColor = (percentage) => {
  if (percentage > 100) return '#00CED1'; // Бирюзовый для перевыполнения
  if (percentage >= 81) return '#52c41a'; // Зелёный для отличного выполнения
  if (percentage >= 50) return '#faad14'; // Жёлтый для среднего выполнения
  return '#ff4d4f'; // Красный для слабого выполнения (<50%)
};

const ManagerMiniCard = ({ manager, currency, plans, kpiOverview, period, planMultiplier = 1 }) => {
  const [expanded, setExpanded] = useState(true);

  // Находим план и KPI для этого менеджера
  const planRow = (plans?.managers || []).find((p) => p.id === manager?.id) || {};
  const normalizeName = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
  const kpiRow = (() => {
    const list = kpiOverview?.managers || [];
    const byId = list.find((k) => k.id === manager?.id);
    if (byId) return byId;
    const byName = list.find((k) => normalizeName(k.name) === normalizeName(manager?.name));
    return byName || {};
  })();

  // Применяем коэффициент к планам менеджера
  // Формула: (месячный_план / дни_в_месяце) * дни_в_периоде = месячный_план * planMultiplier
  const leadsPlan = Math.round(Number(planRow?.leadsPlan || 0) * planMultiplier);
  const qualifiedPlan = Math.round(Number(planRow?.derived?.qualifiedPlan || 0) * planMultiplier);
  const measurePlan = Math.round(Number(planRow?.derived?.measurePlan || 0) * planMultiplier);
  const salesCountPlan = Math.round(Number(planRow?.derived?.salesCountPlan || 0) * planMultiplier);
  const salesSumPlan = Math.round(Number(planRow?.derived?.salesSumPlan || 0) * planMultiplier);

  const leadsFact = Number(kpiRow?.leads || 0);
  const qualifiedFact = Number(kpiRow?.qualified || 0);
  const measureFact = Number(kpiRow?.measure || 0);
  const salesCountFact = Number(kpiRow?.salesCount || 0);
  const salesSumFact = Number(kpiRow?.salesSum || 0);

  const periodLabel = useMemo(() => {
    if (!period?.from || !period?.to) return null;
    const start = moment.tz(period.from, TZ);
    const end = moment.tz(period.to, TZ);
    const sameDay = start.isSame(end, 'day');
    const formatted = sameDay
      ? start.format('DD.MM.YYYY')
      : `${start.format('DD.MM.YYYY')} – ${end.format('DD.MM.YYYY')}`;
    return `за ${formatted}`;
  }, [period?.from, period?.to]);

  // Рассчитываем проценты выполнения для каждого показателя
  const pLeads = percent(leadsFact, leadsPlan);
  const pQual = percent(qualifiedFact, qualifiedPlan);
  const pMeasure = percent(measureFact, measurePlan);
  const pSalesCount = percent(salesCountFact, salesCountPlan);
  const pSalesSum = percent(salesSumFact, salesSumPlan);

  // Главный процент - среднее арифметическое всех показателей
  const avg = Math.round((pLeads + pQual + pMeasure + pSalesCount + pSalesSum) / 5);

  const status = avg <= 30 ? 'Опарыш' : avg <= 65 ? 'Ларечник' : 'Sales';

  return (
    <Card className="rob-card rob-manager-card" hoverable title={manager?.name || 'Менеджер'}>
      <Space align="center" direction="vertical" style={{ width: '100%' }}>
        <div className="rob-speedometer">
          <div
            className="rob-speedometer-circle"
            style={{ '--progress': avg }}
            title={`Выполнение плана: ${avg}%`}
          >
            <div className="rob-speedometer-value">{avg}%</div>
            <div className="rob-speedometer-status">{status}</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Text strong style={{ fontSize: '18px', display: 'block' }}>
            {formatCurrency(salesSumFact, currency)}
          </Text>
          {periodLabel && (
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 2 }}>
              {periodLabel}
            </Text>
          )}
        </div>
      </Space>
      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.key}
            dataSource={[
              { key: 'leads', name: 'Лиды', plan: leadsPlan, fact: leadsFact, isMoney: false, percent: pLeads },
              { key: 'qual', name: 'Квал. лиды', plan: qualifiedPlan, fact: qualifiedFact, isMoney: false, percent: pQual },
              { key: 'measure', name: 'Замеры', plan: measurePlan, fact: measureFact, isMoney: false, percent: pMeasure },
              { key: 'salesCount', name: 'Продажи', plan: salesCountPlan, fact: salesCountFact, isMoney: false, percent: pSalesCount },
              { key: 'salesSum', name: 'Сумма продаж', plan: salesSumPlan, fact: salesSumFact, isMoney: true, percent: pSalesSum },
            ]}
            columns={[
              { title: 'Показатель', dataIndex: 'name', key: 'name' },
              { title: 'План', dataIndex: 'plan', key: 'plan', render: (v, r) => r.isMoney ? formatCurrency(v, currency) : formatNumber(v) },
              { title: 'Факт', dataIndex: 'fact', key: 'fact', render: (v, r) => r.isMoney ? formatCurrency(v, currency) : formatNumber(v) },
              { title: '%', key: 'pct', render: (_, r) => r.percent + '%' },
            ]}
          />
        </div>
      )}
    </Card>
  );
};

// useColumns — не используется, удалено

const KeplerRob = ({ userPermission }) => {
  // Используем глобальное состояние для периода дат
  const { dateRange, periodType, getDateRangeFormatted } = useDateRange();

  // Добавляем флаг для отслеживания первой загрузки
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    currency: "KZT",
    period: {
      from: moment.tz(TZ).startOf("month").toISOString(),
      to: moment.tz(TZ).endOf("month").toISOString(),
    },
    totalPlan: 0,
    totalFact: 0,
    managers: [],
  });

  const currentMonth = useMemo(
    () => moment(metrics?.period?.from || new Date()),
    [metrics?.period?.from]
  );

  // Роль админа и строка месяца нужны до эффектов ниже
  const isAdminView = Boolean(userPermission?.isAdmin || userPermission?.keplerRobAdmin);
  // ИСПРАВЛЕНИЕ: используем TZ для форматирования месяца, чтобы избежать сдвига из-за UTC (например, 2025-11-30T19:00 -> 2025-12)
  const monthStr = useMemo(() => moment.tz(metrics?.period?.from || new Date(), TZ).format('YYYY-MM'), [metrics?.period?.from]);

  const loadData = useCallback(async () => {
    setError(null);
    try {
      // Всегда используем выбранный пользователем диапазон дат (с fallback на текущий месяц)
      const rangeType = dateRange?.[0] && dateRange?.[1] ? 'custom' : 'month';
      const effectiveRange = buildPeriodRange({
        type: rangeType,
        start: dateRange?.[0],
        end: dateRange?.[1],
      });
      const from = effectiveRange.fromUTC;
      const to = effectiveRange.toUTC;


      // Лёгкая версия метрик без тяжёлых include для быстрого рендера
      const raw = await getKeplerRobMetrics({ from, to });
      console.log('✅ [DEBUG] Получены метрики:', {
        managersCount: raw?.managers?.length || 0,
        totalPlan: raw?.totalPlan || raw?.total?.plan,
        totalFact: raw?.totalFact || raw?.total?.fact
      });
      // Нормализация нового ответа (total + managers[].kpi.*)
      const normalized = {
        ...raw,
        period: { from, to },
      };
      if (raw?.total) {
        normalized.totalPlan = Number(raw.total.plan || 0);
        normalized.totalFact = Number(raw.total.fact || 0);
        if (raw.total.percent != null) normalized.totalPercent = Number(raw.total.percent);
      }
      if (Array.isArray(raw?.managers)) {
        normalized.managers = raw.managers.map((mm) => ({
          ...mm,
          fact: (mm?.fact != null ? Number(mm.fact) : Number(mm?.kpi?.salesSum?.fact || 0)),
        }));

      }
      setMetrics(normalized);
      setIsInitialLoad(false);

    } catch (e) {
      console.error('❌ Ошибка загрузки данных:', e);
      setError(e?.message || "Ошибка загрузки данных");
      setIsInitialLoad(false);
    } finally {
    }
  }, [dateRange]);

  useEffect(() => {
    // Убеждаемся, что dateRange готов перед загрузкой
    if (dateRange && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [loadData, dateRange]);

  useEffect(() => {
    changePageTitle("Планы и продажи");
  }, []);

  // removed unused: isDirty, baseline
  // удалены isDirty/baseline

  const onChangeManagerPlan = useCallback((managerId, value) => {
    setMetrics((prev) => ({
      ...prev,
      managers: prev.managers.map((m) =>
        m.id === managerId ? { ...m, plan: Number(value || 0) } : m
      ),
    }));
  }, []);

  // удалены неиспользуемые колонки

  const onMonthChange = async (value) => {
    if (!value) return;
    // сбрасываем пользовательский диапазон при смене месяца
    try { setStatsCustomRange && setStatsCustomRange(null); } catch { }
    const m = moment.utc(value);
    await loadData();
    // Немедленно обновим KPI для вкладки «Статистика» под новый месяц,
    // не дожидаясь эффекта, чтобы избежать гонок
    if (isAdminView && adminTab === 'stats') {
      const monthStart = m.clone().startOf('month');
      const monthEnd = m.clone().endOf('month');
      const ranges = {
        month: { from: monthStart.toISOString(), to: monthEnd.toISOString() },
        w1: { from: monthStart.clone().date(1).startOf('day').toISOString(), to: monthStart.clone().date(10).endOf('day').toISOString() },
        w2: { from: monthStart.clone().date(11).startOf('day').toISOString(), to: monthStart.clone().date(17).endOf('day').toISOString() },
        w3: { from: monthStart.clone().date(18).startOf('day').toISOString(), to: monthStart.clone().date(24).endOf('day').toISOString() },
        w4: { from: monthStart.clone().date(25).startOf('day').toISOString(), to: monthEnd.toISOString() },
      };
      const base = ranges[statsWeek] || ranges.month;
      const from = base.from;
      const to = base.to;
      loadKpi(from, to);
    }
  };

  // Для обычного пользователя подтягиваем его месячный план (salesSumPlan) из /plans
  useEffect(() => {
    if (isAdminView) return; // только не-админ
    const me = metrics?.managers?.[0];
    if (!me) return;
    const loadMyPlan = async () => {
      try {
        const data = await getPlans(monthStr);
        const row = (data?.managers || []).find((m) => (m.id || m.managerId) === me.id);
        if (row) {
          const salesSum = row?.derived?.salesSumPlan;
          if (typeof salesSum === 'number') setMyPlanSalesSum(Number(salesSum || 0));
          else {
            const cv1 = Number(data?.cvTargets?.cv1 || 0);
            const cv2 = Number(data?.cvTargets?.cv2 || 0);
            const cv3 = Number(data?.cvTargets?.cv3 || 0);
            const leads = Number(row?.leadsPlan || 0);
            const avg = Number(row?.avgCheckPlan || 0);
            const qualified = Math.round(leads * (cv1 > 1 ? cv1 / 100 : cv1));
            const measure = Math.round(qualified * (cv2 > 1 ? cv2 / 100 : cv2));
            const salesCount = Math.round(measure * (cv3 > 1 ? cv3 / 100 : cv3));
            setMyPlanSalesSum(salesCount * avg);
            setMyPlan({
              leadsPlan: leads,
              qualifiedPlan: qualified,
              measurePlan: measure,
              salesCountPlan: salesCount,
              salesSumPlan: salesCount * avg,
              avgCheckPlan: avg,
            });
          }
          if (row?.derived) {
            setMyPlan({
              leadsPlan: Number(row.leadsPlan || 0),
              qualifiedPlan: Number(row.derived.qualifiedPlan || 0),
              measurePlan: Number(row.derived.measurePlan || 0),
              salesCountPlan: Number(row.derived.salesCountPlan || 0),
              salesSumPlan: Number(row.derived.salesSumPlan || 0),
              avgCheckPlan: Number(row.avgCheckPlan || 0),
            });
          }
        } else {
          setMyPlanSalesSum(null);
          setMyPlan({ leadsPlan: 0, qualifiedPlan: 0, measurePlan: 0, salesCountPlan: 0, salesSumPlan: 0, avgCheckPlan: 0 });
        }
      } catch {
        setMyPlanSalesSum(null);
        setMyPlan({ leadsPlan: 0, qualifiedPlan: 0, measurePlan: 0, salesCountPlan: 0, salesSumPlan: 0, avgCheckPlan: 0 });
      }
    };
    loadMyPlan();
  }, [isAdminView, metrics?.managers, monthStr]);

  // удалён неиспользуемый onSavePlans

  const [adminTab, setAdminTab] = useState("overview");
  const [funnel, setFunnel] = useState({ stages: [], conversionPct: 0 });
  const [myClients, setMyClients] = useState([]);
  // Планы (цели и входные планы)
  const [plans, setPlans] = useState({ cvTargets: { cv1: 0, cv2: 0, cv3: 0 }, managers: [], totalPlanSalesSum: 0 });
  const [plansLoading, setPlansLoading] = useState(false);
  const [kpi, setKpi] = useState({ managers: [] });
  const [statsWeek, setStatsWeek] = useState('month'); // 'month' | 'w1' | 'w2' | 'w3' | 'w4'
  const [statsCustomRange, setStatsCustomRange] = useState(null); // {from,to}
  const [kpiOverview, setKpiOverview] = useState({ managers: [] });
  const [myPlanSalesSum, setMyPlanSalesSum] = useState(null);
  const [myPlan, setMyPlan] = useState({
    leadsPlan: 0,
    qualifiedPlan: 0,
    measurePlan: 0,
    salesCountPlan: 0,
    salesSumPlan: 0,
    avgCheckPlan: 0,
  });
  const [myKpi, setMyKpi] = useState({
    leads: 0,
    qualified: 0,
    measure: 0,
    salesCount: 0,
    salesSum: 0,
  });

  // Автоматическая синхронизация звонков
  const [autoSync, setAutoSync] = useState(null);
  const [syncStatus, setSyncStatus] = useState({ isRunning: false, lastSync: null, error: null });

  // Модальные окна для редактирования фактов
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedManagerForEdit, setSelectedManagerForEdit] = useState(null);
  const [dailyRefresh, setDailyRefresh] = useState(0);
  const myAvgCheckFact = useMemo(() => {
    const count = Number(myKpi.salesCount || 0);
    const sum = Number(myKpi.salesSum || 0);
    if (!count) return 0;
    const avg = Math.round(sum / count);
    return Number.isFinite(avg) ? avg : 0;
  }, [myKpi.salesCount, myKpi.salesSum]);

  // Синхронизация плана между разделами "Обзор" и "Планы"
  // Объявление effectiveTotalPlan перенесено ниже, после вычисления planTotals
  // Пересчёт derived локально при изменении лидов/среднего чека/целей
  useEffect(() => {
    if (!plans?.managers?.length) return;
    const cv1 = (plans.cvTargets.cv1 || 0) / 100;
    const cv2 = (plans.cvTargets.cv2 || 0) / 100;
    const cv3 = (plans.cvTargets.cv3 || 0) / 100;
    setPlans((prev) => ({
      ...prev,
      managers: prev.managers.map((m) => {
        const leads = Number(m.leadsPlan || 0);
        const avg = Number(m.avgCheckPlan || 0);
        const qualified = Math.round(leads * cv1);
        const measure = Math.round(qualified * cv2);
        const salesCount = Math.round(measure * cv3);
        const salesSum = salesCount * avg;
        return {
          ...m,
          derived: {
            qualifiedPlan: qualified,
            measurePlan: measure,
            salesCountPlan: salesCount,
            salesSumPlan: salesSum,
          },
        };
      }),
    }));
  }, [plans.cvTargets.cv1, plans.cvTargets.cv2, plans.cvTargets.cv3]);

  const planTotals = useMemo(() => {
    const init = { leads: 0, qualified: 0, measure: 0, salesCount: 0, salesSum: 0 };
    return (plans.managers || []).reduce((acc, m) => {
      acc.leads += Number(m.leadsPlan || 0);
      acc.qualified += Number(m?.derived?.qualifiedPlan || 0);
      acc.measure += Number(m?.derived?.measurePlan || 0);
      acc.salesCount += Number(m?.derived?.salesCountPlan || 0);
      acc.salesSum += Number(m?.derived?.salesSumPlan || 0);
      return acc;
    }, init);
  }, [plans.managers]);
  // Вычисляем коэффициент для планов в зависимости от выбранного периода
  // Формула: (месячный_план / дни_в_месяце) * дни_в_выбранном_периоде
  // Для "Месяц" — используем текущий день месяца (прогресс к сегодняшнему дню)
  const planMultiplier = useMemo(() => {
    // Определяем количество дней в месяце (относительно dateRange)
    const referenceDate = dateRange?.[0] || moment.tz(TZ);
    const daysInMonth = referenceDate.daysInMonth();

    // Определяем количество дней в выбранном периоде
    let selectedDays = 1;

    if (periodType === 'month') {
      // Для "Месяц" — план должен быть рассчитан к сегодняшнему дню
      // Например: сегодня 14 декабря → план = (месячный/31) * 14
      const today = moment.tz(TZ);
      const periodMonth = referenceDate.month();
      const currentMonth = today.month();

      if (periodMonth === currentMonth && referenceDate.year() === today.year()) {
        // Текущий месяц — используем номер сегодняшнего дня
        selectedDays = today.date();
      } else {
        // Прошлый или будущий месяц — используем все дни
        selectedDays = daysInMonth;
      }
    } else if (periodType === 'lastMonth') {
      // Для прошлого месяца — полный месяц
      const lastMonth = moment.tz(TZ).subtract(1, 'month');
      selectedDays = lastMonth.daysInMonth();
    } else if (dateRange?.[0] && dateRange?.[1]) {
      // Для любого другого периода (today, yesterday, week, custom)
      // Вычисляем количество дней в выбранном диапазоне
      const startDay = dateRange[0].clone().startOf('day');
      const endDay = dateRange[1].clone().endOf('day');
      selectedDays = endDay.diff(startDay, 'days') + 1;
    }

    // Возвращаем коэффициент: (дней_в_периоде / дней_в_месяце)
    // План будет умножен на этот коэффициент
    const multiplier = selectedDays / daysInMonth;

    console.log('📊 [PLAN_CALC] Расчёт плана:', {
      periodType,
      daysInMonth,
      selectedDays,
      multiplier: multiplier.toFixed(4),
      example: `план ${1000} → ${Math.round(1000 * multiplier)}`
    });

    return multiplier;
  }, [periodType, dateRange]);

  // Скорректированные планы с учётом периода
  // Формула: (месячный_план / дни_в_месяце) * дни_в_периоде
  const adjustedPlanTotals = useMemo(() => {
    return {
      leads: Math.round(planTotals.leads * planMultiplier),
      qualified: Math.round(planTotals.qualified * planMultiplier),
      measure: Math.round(planTotals.measure * planMultiplier),
      salesCount: Math.round(planTotals.salesCount * planMultiplier),
      salesSum: Math.round(planTotals.salesSum * planMultiplier),
    };
  }, [planTotals, planMultiplier]);

  // Используем автоматически рассчитанный план из суммы планов менеджеров, если доступно;
  // иначе используем totalPlanSalesSum из /plans; затем metrics.totalPlan
  // ВАЖНО: используем adjustedPlanTotals (план с учётом периода)
  const effectiveTotalPlan = (adjustedPlanTotals.salesSum && adjustedPlanTotals.salesSum > 0)
    ? adjustedPlanTotals.salesSum
    : (plans.totalPlanSalesSum && plans.totalPlanSalesSum > 0)
      ? Math.round(plans.totalPlanSalesSum * planMultiplier)
      : Math.round(metrics.totalPlan * planMultiplier);

  // Полный месячный план (без коэффициента периода) - для отображения "План на месяц"
  const fullMonthTotalPlan = (planTotals.salesSum && planTotals.salesSum > 0)
    ? planTotals.salesSum
    : (plans.totalPlanSalesSum && plans.totalPlanSalesSum > 0)
      ? plans.totalPlanSalesSum
      : metrics.totalPlan;

  // Итоги фактов из KPI за выбранный период
  const kpiTotals = useMemo(() => {
    const init = { leads: 0, qualified: 0, measure: 0, salesCount: 0, salesSum: 0 };
    const list = kpi.managers || [];
    return list.reduce((acc, r) => {
      acc.leads += Number(r.leads || 0);
      acc.qualified += Number(r.qualified || 0);
      acc.measure += Number(r.measure || 0);
      acc.salesCount += Number(r.salesCount || 0);
      acc.salesSum += Number(r.salesSum || 0);
      return acc;
    }, init);
  }, [kpi.managers]);

  // Итоги фактов для обзора и спидометра
  // Для обычных пользователей - месячные данные, для админов - за выбранный период
  const kpiOverviewTotals = useMemo(() => {
    const init = { leads: 0, qualified: 0, measure: 0, salesCount: 0, salesSum: 0 };
    const source = (kpiOverview?.managers && kpiOverview.managers.length > 0)
      ? kpiOverview.managers
      : (kpi.managers || []);
    return source.reduce((acc, r) => {
      acc.leads += Number(r.leads || 0);
      acc.qualified += Number(r.qualified || 0);
      acc.measure += Number(r.measure || 0);
      acc.salesCount += Number(r.salesCount || 0);
      acc.salesSum += Number(r.salesSum || 0);
      return acc;
    }, init);
  }, [kpiOverview.managers, kpi.managers]);

  // Факт берём из kpiOverview (для админа - за период, для пользователя - за месяц)
  // ВАЖНО: используем явную проверку, чтобы 0 не подменялся месячным totalFact
  const effectiveTotalFact = (kpiOverviewTotals.salesSum ?? null) !== null
    ? kpiOverviewTotals.salesSum
    : metrics.totalFact;
  // Процент выполнения всегда считаем от актуальных значений (а не из метрик)
  const totalPercent = percent(effectiveTotalFact, effectiveTotalPlan);
  // Процент выполнения от полного месячного плана
  const fullMonthPercent = percent(effectiveTotalFact, fullMonthTotalPlan);

  // Хелпер: должен ли менеджер отображаться
  // ИСПРАВЛЕНИЕ: Показываем менеджеров с планом ИЛИ фактом (чтобы можно было видеть данные за прошлые периоды)
  const shouldShowManager = useCallback(
    (managerId, managerFact) => {
      // Если планы еще не загружены, показываем всех (временно)
      if (!plans.managers || plans.managers.length === 0) {
        console.log(`⚠️ Планы не загружены, временно показываем менеджера: ${managerId}`);
        return true; // Показываем пока планы не загрузятся
      }

      const row = plans.managers.find((x) => x.id === managerId);

      // Проверяем наличие плана
      let hasPlan = false;
      if (row) {
        const salesSumPlan = row?.derived?.salesSumPlan;
        const leadsPlan = row?.leadsPlan;
        const avgCheckPlan = row?.avgCheckPlan;

        const hasSalesPlan = Number(salesSumPlan || 0) > 0;
        const hasInputPlans = Number(leadsPlan || 0) > 0 && Number(avgCheckPlan || 0) > 0;
        hasPlan = hasSalesPlan || hasInputPlans;
      }

      // НОВАЯ ЛОГИКА: показываем менеджера, если у него есть ПЛАН или ФАКТ
      const hasFact = Number(managerFact || 0) > 0;
      const shouldShow = hasPlan || hasFact;

      // Отладочная информация
      console.log(`🔍 Менеджер ${row?.name || managerId}: план=${hasPlan}, факт=${managerFact}, показ=${shouldShow}`);

      return shouldShow;
    },
    [plans.managers]
  );

  // Отображаемые планы в карточках продаж и таблице замеров: берём из вкладки «Планы», если доступны
  const managersDisplay = useMemo(() => {


    const normalizeName = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
    const plansById = new Map((plans.managers || []).map((m) => [m.id, m]));

    const base = (kpi.managers && kpi.managers.length > 0) ? kpi.managers : (metrics.managers || []);

    const result = base
      .map((row) => {
        const pm = plansById.get(row.id)
          || (plans.managers || []).find((p) => normalizeName(p.name) === normalizeName(row.name));
        const planOverride = pm?.derived?.salesSumPlan;
        const avgCheckPlan = Number(pm?.avgCheckPlan || 0);
        const salesSumFact = Number(row?.salesSum || 0);
        const salesCountFact = Number(row?.salesCount || 0);
        const avgCheckFact = salesCountFact ? Math.round(salesSumFact / salesCountFact) : 0;
        const extended = {
          id: row.id,
          name: row.name,
          plan: Number(row.plan || 0),
          fact: salesSumFact,
          kpi: { salesSum: { fact: salesSumFact }, salesCount: { fact: salesCountFact } },
          avgCheckPlan,
          avgCheckFact,
        };
        return isAdminView && planOverride != null ? { ...extended, plan: planOverride } : extended;
      })
      .filter((m) => {
        const shouldShow = shouldShowManager(m.id, m.fact);
        if (!shouldShow) {
          console.log(`Скрываем менеджера ${m.name} (ID: ${m.id}) - нет плана и факта`);
        }
        return shouldShow;
      })
      .sort((a, b) => {
        const order = ['жасулан', 'алибек', 'санат', 'арай', 'алишер', 'мариям'];
        const getIndex = (name) => {
          const lower = (name || '').toLowerCase();
          const index = order.findIndex(key => lower.includes(key));
          return index === -1 ? 999 : index;
        };
        return getIndex(a.name) - getIndex(b.name);
      });

    return result;
  }, [kpi.managers, metrics.managers, plans.managers, isAdminView, shouldShowManager]);


  // Для админа список менеджеров приходит из /metrics и уже содержит корректные id

  // воронка отключена до появления рабочего бэкенд‑эндоинта

  // загрузка клиентов текущего менеджера (если не админ)
  useEffect(() => {
    if (!isAdminView && (userPermission?.keplerRob || userPermission?.salesDepartment)) {
      const baseUrl = process.env.REACT_APP_API_BASE || `${require("../../config.json").service}/api`;
      const params = new URLSearchParams();
      params.append('assignedTo', 'me');
      params.append('size', '1000');

      const { startISO, endISO } = getDateRangeFormatted();
      if (startISO && endISO) {
        params.append('from', startISO);
        params.append('to', endISO);
      }

      const url = `${baseUrl}/client?${params.toString()}`;
      getRequest(url)
        .then((resp) => {
          const list = resp?.data?.clients || resp?.data || [];
          setMyClients(list);
        })
        .catch(() => setMyClients([]));

      // Подгружаем KPI для текущего пользователя (глобальный период)
      // Для не-админа используем предустановленный период month через ?period=
      getKpi({ period: 'month' })
        .then((data) => {
          const meId = metrics?.managers?.[0]?.id;
          const managers = Array.isArray(data?.managers) ? data.managers : [];
          const row = (managers.find((x) => x.id === meId)) || managers[0] || {};
          setMyKpi({
            leads: Number(row.leads || 0),
            qualified: Number(row.qualified || 0),
            measure: Number(row.measure || 0),
            salesCount: Number(row.salesCount || 0),
            salesSum: Number(row.salesSum || 0),
          });
        })
        .catch(() => setMyKpi({ leads: 0, qualified: 0, measure: 0, salesCount: 0, salesSum: 0 }));
    }
  }, [
    isAdminView,
    userPermission?.keplerRob,
    userPermission?.salesDepartment,
    getDateRangeFormatted,
    metrics?.managers,
    dateRange?.[0]?.valueOf(),
    dateRange?.[1]?.valueOf(),
  ]);

  const filteredMyClients = useMemo(() => {
    if (!Array.isArray(myClients)) {
      return [];
    }
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return myClients;
    }
    const start = dateRange[0].clone().tz(TZ).startOf('day');
    const end = dateRange[1].clone().tz(TZ).endOf('day');

    return myClients.filter((client) => {
      const created = moment(client?.createdAt);
      if (!created.isValid()) return true;
      const createdTz = created.clone().tz(TZ);
      return createdTz.isBetween(start, end, undefined, '[]');
    });
  }, [myClients, dateRange]);

  // Загрузка планов при входе во вкладку «Планы»
  // ИСПРАВЛЕНИЕ: планы загружаются для выбранного периода, используя dateRange напрямую
  const loadPlans = useCallback(async (silent = false) => {
    if (!isAdminView) {
      console.log('⏭️ Пропуск загрузки планов - не админ');
      return;
    }
    if (!silent) setPlansLoading(true);
    try {
      // Определяем месяц для загрузки планов из dateRange (приоритет) или metrics.period.from (fallback)
      let selectedMonth;
      if (dateRange && dateRange[0]) {
        // Используем dateRange напрямую - это источник истины
        // ВАЖНО: форматируем локально, чтобы избежать сдвига часового пояса (UTC может быть вчерашним днем)
        selectedMonth = dateRange[0].format('YYYY-MM');
      } else if (metrics?.period?.from) {
        // Fallback на metrics.period.from
        selectedMonth = moment(metrics.period.from).format('YYYY-MM');
      } else {
        // Последний fallback - текущий месяц
        selectedMonth = moment.tz(TZ).format('YYYY-MM');
      }

      console.log('🔄 Загрузка планов для месяца:', selectedMonth, {
        dateRangeStart: dateRange?.[0]?.format('YYYY-MM-DD'),
        periodFrom: metrics?.period?.from
      });

      const data = await getPlans(selectedMonth);
      console.log('✅ Получены планы:', data);
      console.log('📊 Подробные данные планов:', {
        month: selectedMonth,
        cvTargets: data?.cvTargets,
        totalPlanSalesSum: data?.totalPlanSalesSum,
        managersCount: data?.managers?.length,
        firstManager: data?.managers?.[0]
      });
      const cv = data?.cvTargets || { cv1: 0, cv2: 0, cv3: 0 };
      const toPct = (v) => (v <= 1 ? Math.round((v || 0) * 100) : Math.round(v || 0));
      const incomingManagers = Array.isArray(data?.managers) ? data.managers : [];
      const fallbackManagers = (metrics.managers || []).map((mm) => ({ id: mm.id, name: mm.name }));
      const merged = (incomingManagers.length ? incomingManagers : fallbackManagers).map((m) => ({
        id: m.id || m.managerId,
        name: m.name,
        leadsPlan: m.leadsPlan ?? 0,
        avgCheckPlan: m.avgCheckPlan ?? 0,
        derived: m.derived || { qualifiedPlan: 0, measurePlan: 0, salesCountPlan: 0, salesSumPlan: 0 },
      }));
      console.log('📋 Обработано планов для менеджеров:', merged.length);
      console.log('📋 Первый менеджер после обработки:', merged[0]);
      setPlans({
        cvTargets: { cv1: toPct(cv.cv1), cv2: toPct(cv.cv2), cv3: toPct(cv.cv3) },
        managers: merged,
        totalPlanSalesSum: Number(data?.totalPlanSalesSum || 0),
      });
    } catch (err) {
      console.error('❌ Ошибка загрузки планов:', err);
    } finally {
      if (!silent) setPlansLoading(false);
    }
  }, [isAdminView, metrics.managers, metrics?.period?.from, dateRange]);

  useEffect(() => {
    if (isAdminView && (adminTab === 'plans' || adminTab === 'overview')) {
      loadPlans();
    }
  }, [isAdminView, adminTab, loadPlans]);

  // Предзагрузка планов в фоне для ускорения открытия вкладок «Планы» и «Обзор»
  useEffect(() => {
    if (!isAdminView) return;
    if (adminTab !== 'plans' && adminTab !== 'overview' && (!plans.managers || plans.managers.length === 0)) {
      loadPlans(true);
    }
  }, [isAdminView, adminTab, loadPlans]);

  // Принудительная загрузка планов при первом рендере для корректной фильтрации менеджеров
  // Загружаем только после того, как основные метрики загружены
  useEffect(() => {
    console.log('🔍 Проверка необходимости загрузки планов:', {
      isAdminView,
      isInitialLoad,
      hasMetricsManagers: metrics.managers?.length > 0,
      hasPlansManagers: plans.managers?.length > 0,
      metricsManagersCount: metrics.managers?.length,
      plansManagersCount: plans.managers?.length
    });

    if (isAdminView && !isInitialLoad && metrics.managers && metrics.managers.length > 0 && (!plans.managers || plans.managers.length === 0)) {
      console.log('🚀 Загружаем планы для фильтрации менеджеров...');
      loadPlans(true);
    }
  }, [isAdminView, isInitialLoad, metrics.managers, plans.managers, loadPlans]);

  // Обработчики модальных окон редактирования фактов
  const handleOpenEditModal = (manager, date) => {
    setSelectedManagerForEdit(date ? { ...manager, initialDate: date } : manager);
    setEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedManagerForEdit(null);
  };

  const handleSaveManualFacts = async (managerId, date, facts) => {
    try {
      await saveManualFacts({ managerId, date, facts });
      // Перезагружаем KPI данные и таблицу параллельно
      const { startISO, endISO } = getDateRangeFormatted();
      loadKpi(startISO, endISO); // Запускаем обновление графиков в фоне
      setDailyRefresh(prev => prev + 1); // Запускаем обновление таблицы
      message.success('Факты успешно сохранены');
    } catch (error) {
      console.error('Ошибка сохранения фактов:', error);
      const backendMessage = error?.message || error?.response?.data?.message || error?.response?.data?.error;
      message.error(backendMessage ? `Не удалось сохранить факты: ${backendMessage}` : 'Не удалось сохранить факты');
      throw error;
    }
  };

  const handleOpenHistoryModal = (manager) => {
    setSelectedManagerForEdit(manager);
    setHistoryModalVisible(true);
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalVisible(false);
    setSelectedManagerForEdit(null);
  };

  // Загрузка KPI по выбранному периоду для вкладки «Статистика»
  const loadKpi = useCallback(async (from, to) => {
    try {
      // Гвард: если диапазон не рассчитан, используем пресет month
      if (!from || !to) {

        const data = await getKpi({ period: 'month' });
        setKpi(data);
        return;
      }


      // Определяем предустановленный период для ?period=
      const tzNow = moment.tz(TZ);
      const startDay = moment.tz(from, TZ).startOf('day');
      const endDay = moment.tz(to, TZ).endOf('day');
      let periodParam = null;
      if (startDay.isSame(tzNow.clone().startOf('day')) && endDay.isSame(tzNow.clone().endOf('day'))) {
        periodParam = 'today';
      } else if (startDay.isSame(tzNow.clone().subtract(1, 'day').startOf('day')) && endDay.isSame(tzNow.clone().subtract(1, 'day').endOf('day'))) {
        periodParam = 'yesterday';
      } else if (startDay.isSame(tzNow.clone().subtract(6, 'day').startOf('day')) && endDay.isSame(tzNow.clone().endOf('day'))) {
        periodParam = 'week';
      } else if (startDay.isSame(tzNow.clone().startOf('month')) && endDay.isSame(tzNow.clone().endOf('month'))) {
        periodParam = 'month';
      }

      const data = await (periodParam
        ? getKpi({ period: periodParam })
        : getKpi({ from, to, normalize: true }));
      const managersCount = data?.managers?.length || 0;
      const managerIds = data?.managers?.slice(0, 2).map(m => m.id) || [];

      // Проверка на пустые данные
      const allZeros = data?.managers?.every(m =>
        (m.leads === 0 || !m.leads) &&
        (m.qualified === 0 || !m.qualified) &&
        (m.measure === 0 || !m.measure) &&
        (m.salesCount === 0 || !m.salesCount) &&
        (m.calls?.incomingMinutes === 0 || !m.calls?.incomingMinutes) &&
        (m.calls?.outgoingMinutes === 0 || !m.calls?.outgoingMinutes)
      );





      setKpi(data);
    } catch (error) {
      console.error('❌ Failed to load KPI data:', { from, to, error });
      setKpi({ managers: [] });
    }
  }, [adminTab, statsWeek]);

  // Обновляем KPI при смене периода/вкладки/недели
  useEffect(() => {
    if (!isAdminView || adminTab !== 'stats') return;

    // Используем глобальный диапазон дат как базовый период (нормализуем через buildPeriodRange)
    const adminRange = buildPeriodRange({ type: 'custom', start: dateRange?.[0], end: dateRange?.[1] });
    const monthStart = moment.tz(adminRange.fromUTC, TZ).startOf('month');
    const monthEnd = moment.tz(adminRange.toUTC, TZ).endOf('month');

    const ranges = {
      month: { from: adminRange.fromUTC, to: adminRange.toUTC },
      w1: { from: monthStart.clone().date(1).startOf('day').toISOString(), to: monthStart.clone().date(10).endOf('day').toISOString() },
      w2: { from: monthStart.clone().date(11).startOf('day').toISOString(), to: monthStart.clone().date(17).endOf('day').toISOString() },
      w3: { from: monthStart.clone().date(18).startOf('day').toISOString(), to: monthStart.clone().date(24).endOf('day').toISOString() },
      w4: { from: monthStart.clone().date(25).startOf('day').toISOString(), to: monthEnd.toISOString() },
    };
    const base = ranges[statsWeek] || ranges.month;
    // Нормализуем итоговый период через buildPeriodRange, чтобы получить ISO UTC
    const finalRange = buildPeriodRange({ type: 'custom', start: statsCustomRange?.from || base.from, end: statsCustomRange?.to || base.to });
    const from = finalRange.fromUTC;
    const to = finalRange.toUTC;
    loadKpi(from, to);
  }, [isAdminView, adminTab, statsWeek, statsCustomRange?.from, statsCustomRange?.to, dateRange, loadKpi]);

  // KPI для обзора - для админов используем глобальный период, для обычных пользователей - месячный
  // Загружаем сразу после завершения начальной загрузки метрик и при смене периода
  useEffect(() => {
    if (isInitialLoad) return;
    console.log('🔄 Загрузка KPI для обзора...');

    // Для обычных пользователей всегда используем месячный период (из metrics.period)
    // Для админов используем глобальный период из dateRange
    const adminRange = buildPeriodRange({ type: 'custom', start: dateRange?.[0], end: dateRange?.[1] });
    const monthRange = { fromUTC: metrics.period.from, toUTC: metrics.period.to };
    const from = isAdminView ? adminRange.fromUTC : monthRange.fromUTC;
    const to = isAdminView ? adminRange.toUTC : monthRange.toUTC;

    console.log('📅 [DEBUG] Период для обзора:', {
      from,
      to,
      isAdminView,
      periodType: from === to ? 'single day' : moment.utc(to).diff(moment.utc(from), 'days') + ' days'
    });
    (async () => {
      try {
        // Пытаемся использовать предустановленный период, если совпадает
        const tzNow = moment.tz(TZ);
        const startDay = moment.tz(from, TZ).startOf('day');
        const endDay = moment.tz(to, TZ).endOf('day');
        let periodParam = null;
        if (startDay.isSame(tzNow.clone().startOf('day')) && endDay.isSame(tzNow.clone().endOf('day'))) {
          periodParam = 'today';
        } else if (startDay.isSame(tzNow.clone().subtract(1, 'day').startOf('day')) && endDay.isSame(tzNow.clone().subtract(1, 'day').endOf('day'))) {
          periodParam = 'yesterday';
        } else if (startDay.isSame(tzNow.clone().subtract(6, 'day').startOf('day')) && endDay.isSame(tzNow.clone().endOf('day'))) {
          periodParam = 'week';
        } else if (startDay.isSame(tzNow.clone().startOf('month')) && endDay.isSame(tzNow.clone().endOf('month'))) {
          periodParam = 'month';
        }

        const data = await (
          periodParam ? getKpi({ period: periodParam }) : getKpi({ from, to, normalize: true })
        );
        const managersCount = data?.managers?.length || 0;
        console.log('✅ [DEBUG] KPI для обзора загружены:', {
          managersCount,
          managerIds: data?.managers?.slice(0, 2).map(m => m.id) || [],
          hasData: managersCount > 0 && data?.managers?.some(m =>
            m.leads > 0 || m.qualified > 0 || m.measure > 0 || m.salesCount > 0
          )
        });
        setKpiOverview(data || { managers: [] });
      } catch (err) {
        console.error('❌ Ошибка загрузки KPI для обзора:', { from, to, err });
        setKpiOverview({ managers: [] });
      }
    })();
  }, [isInitialLoad, isAdminView, dateRange, getDateRangeFormatted, metrics.period]);

  // Инициализация автоматической синхронизации звонков
  useEffect(() => {
    if (!isAdminView) return; // Только для админов

    const sync = new SipuniAutoSync(60); // Каждые 60 минут

    sync.onSyncComplete = (result) => {
      setSyncStatus(prev => ({
        ...prev,
        lastSync: new Date(),
        error: null
      }));
      console.log('Автоматическая синхронизация звонков завершена:', result);

      // Обновляем KPI данные после синхронизации
      if (adminTab === 'stats') {
        const { startISO, endISO } = getDateRangeFormatted();
        loadKpi(startISO, endISO);
      }
    };

    sync.onSyncError = (error) => {
      setSyncStatus(prev => ({
        ...prev,
        error: error.message || 'Ошибка синхронизации'
      }));
      console.error('Ошибка автоматической синхронизации звонков:', error);
    };

    setAutoSync(sync);
    setSyncStatus(prev => ({ ...prev, isRunning: true }));
    sync.start();

    // Очистка при размонтировании
    return () => {
      if (sync) {
        sync.stop();
        setSyncStatus(prev => ({ ...prev, isRunning: false }));
      }
    };
  }, [isAdminView, adminTab, getDateRangeFormatted, loadKpi]);

  // Функция для ручной синхронизации
  const handleManualSync = useCallback(async () => {
    try {
      setSyncStatus(prev => ({ ...prev, error: null }));

      const { startISO, endISO } = getDateRangeFormatted();
      const startDate = startISO.split('T')[0];
      const endDate = endISO.split('T')[0];

      const result = await syncCalls(startDate, endDate);

      setSyncStatus(prev => ({
        ...prev,
        lastSync: new Date(),
        error: null
      }));

      message.success('Синхронизация звонков выполнена успешно');

      // Обновляем KPI данные
      if (adminTab === 'stats') {
        loadKpi(startISO, endISO);
      }

      return result;
    } catch (error) {
      const errorMsg = error.message || 'Ошибка синхронизации звонков';
      setSyncStatus(prev => ({ ...prev, error: errorMsg }));
      message.error(errorMsg);
      throw error;
    }
  }, [getDateRangeFormatted, adminTab, loadKpi]);

  const savePlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const { cvTargets, managers } = plans;
      const toFraction = (v) => (v > 1 ? Number(v) / 100 : Number(v || 0));

      // Автоматически рассчитываем общий план продаж из суммы планов менеджеров
      const calculatedTotalPlanSalesSum = planTotals.salesSum;

      const body = {
        cvTargets: { cv1: toFraction(cvTargets.cv1), cv2: toFraction(cvTargets.cv2), cv3: toFraction(cvTargets.cv3) },
        totalPlanSalesSum: Number(calculatedTotalPlanSalesSum || 0),
        managers: managers.map((m) => ({ managerId: m.id, leadsPlan: Number(m.leadsPlan || 0), avgCheckPlan: Number(m.avgCheckPlan || 0) })),
      };

      console.log('💾 Сохранение планов с автоматически рассчитанной суммой:', calculatedTotalPlanSalesSum);

      // Всегда сохраняем планы для ВЫБРАННОГО месяца (а не текущего)
      // ИСПРАВЛЕНИЕ: Приоритет отдаем dateRange (выбор пользователя), затем monthStr
      let targetMonth;
      if (dateRange && dateRange[0]) {
        targetMonth = dateRange[0].format('YYYY-MM');
      } else {
        targetMonth = monthStr || moment.tz(TZ).format('YYYY-MM');
      }

      console.log('📅 Сохранение планов для месяца:', targetMonth);
      await putPlans(targetMonth, body);
      message.success('Планы сохранены');

      // Обновляем локальное состояние с рассчитанной суммой
      setPlans((prev) => ({ ...prev, totalPlanSalesSum: calculatedTotalPlanSalesSum }));

      // После сохранения обновим метрики и планы
      await Promise.all([loadData(), loadPlans(true)]);

      // Обновляем локальное состояние планов для немедленного отображения в обзоре
      // План в обзоре теперь берется из plans.totalPlanSalesSum через effectiveTotalPlan
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Не удалось сохранить планы');
    } finally { setPlansLoading(false); }
  }, [plans, planTotals.salesSum, monthStr, loadData, loadPlans, metrics?.period?.from]);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {/* Глобальный календарь для выбора периода */}
      <GlobalDateRangePicker />

      {/* Заголовок страницы */}
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={3} style={{ margin: 0 }}>
            Kepler Sales Hub — планы и продажи
          </Title>
          <Text type="secondary">
            Период: {dateRange[0].format("DD.MM.YYYY")} - {dateRange[1].format("DD.MM.YYYY")}
          </Text>
        </Col>
        {isAdminView && (
          <Col />
        )}
      </Row>

      {/* Индикатор загрузки при первой загрузке */}
      {isInitialLoad && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Загрузка данных...</Text>
            </div>
          </div>
        </Card>
      )}

      {!isInitialLoad && !isAdminView && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={8}>
              <Card>
                <Space direction="vertical" style={{ width: "100%" }} align="center">
                  <Title level={4} style={{ marginBottom: 0 }}>
                    Мои продажи
                  </Title>
                  <Progress type="dashboard" percent={percent(metrics?.managers?.[0]?.fact || 0, myPlanSalesSum || metrics?.managers?.[0]?.plan || 0)} size={280} />
                  <Space size={24} wrap>
                    <Statistic
                      title="Факт"
                      value={metrics?.managers?.[0]?.fact || 0}
                      formatter={(v) => formatCurrency(v, metrics.currency)}
                    />
                    <div style={{ textAlign: "center" }}>
                      <Text type="secondary">План</Text>
                      <div style={{ marginTop: 6, fontSize: 20, fontWeight: 500 }}>
                        {formatCurrency(myPlanSalesSum || metrics?.managers?.[0]?.plan || 0, metrics.currency)}
                      </div>
                    </div>
                  </Space>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12} lg={16}>
              <Card title="Моя статистика (месяц)">
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={8}>
                    <Statistic title="Лиды — план" value={myPlan.leadsPlan} />
                    <Statistic title="Лиды — факт" value={myKpi.leads} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic title="Квал лиды — план" value={myPlan.qualifiedPlan} />
                    <Statistic title="Квал лиды — факт" value={myKpi.qualified} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic title="Замеры — план" value={myPlan.measurePlan} />
                    <Statistic title="Замеры — факт" value={myKpi.measure} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic title="Продажи — план" value={myPlan.salesCountPlan} />
                    <Statistic title="Продажи — факт" value={myKpi.salesCount} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic title="Сумма продаж — план" value={myPlan.salesSumPlan} formatter={(v) => formatCurrency(v, metrics.currency)} />
                    <Statistic title="Сумма продаж — факт" value={myKpi.salesSum} formatter={(v) => formatCurrency(v, metrics.currency)} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Statistic title="Средний чек (план)" value={myPlan.avgCheckPlan} formatter={(v) => formatCurrency(v, metrics.currency)} />
                    <Statistic title="Средний чек (факт)" value={(metrics?.managers?.[0]?.kpi?.avgCheck?.fact ?? myAvgCheckFact) || 0} formatter={(v) => formatCurrency(v, metrics.currency)} />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Мои клиенты (как на странице Клиенты) */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card className="rob-card" title="Мои клиенты">
                <Table
                  bordered
                  size="small"
                  pagination={false}
                  columns={require("../Clients/utils/table").getTableColumns()}
                  dataSource={require("../Clients/utils/table").getTableDataSource(filteredMyClients)}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {!isInitialLoad && isAdminView && (
        <Tabs activeKey={adminTab} onChange={setAdminTab}>
          <TabPane tab="Обзор" key="overview">
            <div className="rob-dashboard">
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                  <Card className="rob-card" title="Выполнение плана продаж" style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', transform: 'scale(1.8)', transformOrigin: 'center', padding: '80px 0', overflow: 'visible' }}>
                        <Progress type="dashboard" percent={totalPercent} size={200} />
                      </div>
                      {/* Факт и План (к текущей дате) */}
                      <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', width: '100%', overflow: 'visible', flexWrap: 'wrap' }}>
                        <Statistic
                          title="Факт"
                          value={effectiveTotalFact}
                          formatter={(v) => formatCurrency(v, metrics.currency)}
                          valueStyle={{ fontSize: '1.6em' }}
                        />
                        <Tooltip title="Пропорциональный план к текущей дате">
                          <Statistic
                            title="План"
                            value={effectiveTotalPlan}
                            formatter={(v) => formatCurrency(v, metrics.currency)}
                            valueStyle={{ fontSize: '1.6em' }}
                          />
                        </Tooltip>
                      </div>
                      {/* Полный план на месяц */}
                      <div style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>Выполнение месяца</Text>
                          <div style={{
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            color: getCompletionColor(fullMonthPercent)
                          }}>
                            {fullMonthPercent}%
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>План на месяц</Text>
                          <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                            {formatCurrency(fullMonthTotalPlan, metrics.currency)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>

                {/* Правый блок - Структурированные показатели */}
                <Col xs={24} lg={16}>
                  <Card className="rob-card" title="Структурированные показатели (план → факт → % выполнения)" style={{ height: '100%' }}>
                    <div style={{ padding: '20px 0' }}>
                      {/* Лиды */}
                      <Row gutter={[16, 16]} style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#e6f7ff', borderRadius: '8px' }}>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                              {formatNumber(adjustedPlanTotals.leads)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ПЛАН ЛИДОВ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: getCompletionColor(percent(kpiOverviewTotals.leads, adjustedPlanTotals.leads)), marginBottom: '4px' }}>
                              {formatNumber(kpiOverviewTotals.leads)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ФАКТ ЛИДОВ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '4px' }}>
                              {percent(kpiOverviewTotals.leads, adjustedPlanTotals.leads)}%
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ВЫПОЛНЕНИЕ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '4px' }}>
                              {adjustedPlanTotals.leads - kpiOverviewTotals.leads > 0 ? `-${adjustedPlanTotals.leads - kpiOverviewTotals.leads}` : '+' + (kpiOverviewTotals.leads - adjustedPlanTotals.leads)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ДО ПЛАНА</Text>
                          </div>
                        </Col>
                      </Row>

                      {/* Квалифицированные лиды */}
                      <Row gutter={[16, 16]} style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f6ffed', borderRadius: '8px' }}>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                              {formatNumber(adjustedPlanTotals.qualified)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ПЛАН КВАЛ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: getCompletionColor(percent(kpiOverviewTotals.qualified, adjustedPlanTotals.qualified)), marginBottom: '4px' }}>
                              {formatNumber(kpiOverviewTotals.qualified)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ФАКТ КВАЛ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '4px' }}>
                              {percent(kpiOverviewTotals.qualified, adjustedPlanTotals.qualified)}%
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ВЫПОЛНЕНИЕ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '4px' }}>
                              {adjustedPlanTotals.qualified - kpiOverviewTotals.qualified > 0 ? `-${adjustedPlanTotals.qualified - kpiOverviewTotals.qualified}` : '+' + (kpiOverviewTotals.qualified - adjustedPlanTotals.qualified)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ДО ПЛАНА</Text>
                          </div>
                        </Col>
                      </Row>

                      {/* Замеры */}
                      <Row gutter={[16, 16]} style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9f0ff', borderRadius: '8px' }}>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                              {formatNumber(adjustedPlanTotals.measure)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ПЛАН ЗАМЕРОВ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: getCompletionColor(percent(kpiOverviewTotals.measure, adjustedPlanTotals.measure)), marginBottom: '4px' }}>
                              {formatNumber(kpiOverviewTotals.measure)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ФАКТ ЗАМЕРОВ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '4px' }}>
                              {percent(kpiOverviewTotals.measure, adjustedPlanTotals.measure)}%
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ВЫПОЛНЕНИЕ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '4px' }}>
                              {adjustedPlanTotals.measure - kpiOverviewTotals.measure > 0 ? `-${adjustedPlanTotals.measure - kpiOverviewTotals.measure}` : '+' + (kpiOverviewTotals.measure - adjustedPlanTotals.measure)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ДО ПЛАНА</Text>
                          </div>
                        </Col>
                      </Row>

                      {/* Продажи */}
                      <Row gutter={[16, 16]} style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#fff7e6', borderRadius: '8px' }}>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                              {formatNumber(adjustedPlanTotals.salesCount)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ПЛАН ПРОДАЖ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: getCompletionColor(percent(kpiOverviewTotals.salesCount, adjustedPlanTotals.salesCount)), marginBottom: '4px' }}>
                              {formatNumber(kpiOverviewTotals.salesCount)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ФАКТ ПРОДАЖ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '4px' }}>
                              {percent(kpiOverviewTotals.salesCount, adjustedPlanTotals.salesCount)}%
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ВЫПОЛНЕНИЕ</Text>
                          </div>
                        </Col>
                        <Col span={6}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '4px' }}>
                              {adjustedPlanTotals.salesCount - kpiOverviewTotals.salesCount > 0 ? `-${adjustedPlanTotals.salesCount - kpiOverviewTotals.salesCount}` : '+' + (kpiOverviewTotals.salesCount - adjustedPlanTotals.salesCount)}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ДО ПЛАНА</Text>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Карточки менеджеров */}
              {(!managersDisplay || managersDisplay.length === 0) && (
                <Alert
                  type="info"
                  message="Нет данных для отображения"
                  description="Менеджеры будут отображаться, если у них есть план продаж или факт продаж за выбранный период. Перейдите на вкладку 'Планы' чтобы установить планы для менеджеров."
                  style={{ marginTop: '24px' }}
                />
              )}
              <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
                {(() => {
                  console.log('🎴 Рендер карточек менеджеров:', {
                    count: managersDisplay?.length || 0,
                    managers: managersDisplay?.map(m => ({ id: m.id, name: m.name, fact: m.fact }))
                  });
                  return (managersDisplay || []).map((m) => (
                    <Col key={m.id} xs={24} sm={12} md={12} lg={8} xl={6}>
                      <ManagerMiniCard
                        manager={m}
                        currency={metrics.currency}
                        plans={plans}
                        kpiOverview={{ managers: (kpiOverview?.managers && kpiOverview.managers.length > 0) ? kpiOverview.managers : (kpi.managers || []) }}
                        period={metrics?.period}
                        planMultiplier={planMultiplier}
                      />
                    </Col>
                  ));
                })()}
              </Row>
            </div>
          </TabPane>
          <TabPane tab="Планы" key="plans">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card>
                  <Space size={24} align="center" wrap>
                    <Statistic
                      title="Общий план продаж (автоматически)"
                      value={planTotals.salesSum}
                      formatter={(v) => formatCurrency(v, metrics.currency)}
                      valueStyle={{ color: '#1890ff' }}
                    />
                    {[
                      { key: "cv1", label: "CV1 цель, %" },
                      { key: "cv2", label: "CV2 цель, %" },
                      { key: "cv3", label: "CV3 цель, %" },
                    ].map(({ key, label }) => (
                      <Space key={key} direction="vertical" size={4} style={{ alignItems: "center" }}>
                        <Text type="secondary">{label}</Text>
                        <InputNumber
                          min={0}
                          max={100}
                          value={plans?.cvTargets?.[key]}
                          onChange={(v) =>
                            setPlans((p) => ({
                              ...p,
                              cvTargets: { ...p.cvTargets, [key]: Number(v || 0) },
                            }))
                          }
                        />
                      </Space>
                    ))}
                    <Button type="primary" onClick={savePlans} loading={plansLoading}>
                      Сохранить планы
                    </Button>
                  </Space>
                </Card>
              </Col>
              <Col span={24}>
                <Card title="Планы по сотрудникам">
                  <Table
                    rowKey={(r) => r.id}
                    pagination={false}
                    dataSource={plans.managers}
                    loading={plansLoading}
                    columns={[
                      { title: "Пользователь", dataIndex: "name", key: "name", width: 200 },
                      {
                        title: "ЛИДЫ", dataIndex: "leadsPlan", key: "leadsPlan", width: 120, render: (v, r) => (
                          <InputNumber min={0} parser={stripLeadingZerosParser} value={r.leadsPlan} onFocus={(e) => { if (String(r.leadsPlan || 0) === '0') e.target.select(); }} onChange={(x) => setPlans((p) => ({ ...p, managers: p.managers.map(m => m.id === r.id ? { ...m, leadsPlan: Number(x || 0) } : m) }))} />
                        )
                      },
                      { title: "КВАЛ ЛИДЫ", dataIndex: ["derived", "qualifiedPlan"], key: "qualifiedPlan", width: 140 },
                      { title: "ЗАМЕРЫ", dataIndex: ["derived", "measurePlan"], key: "measurePlan", width: 140 },
                      { title: "ПРОДАЖИ", dataIndex: ["derived", "salesCountPlan"], key: "salesCountPlan", width: 140 },
                      { title: "СУММА ПРОДАЖ", dataIndex: ["derived", "salesSumPlan"], key: "salesSumPlan", width: 160, render: (v) => formatCurrency(v || 0, metrics.currency) },
                      {
                        title: "СРЕДНИЙ ЧЕК", dataIndex: "avgCheckPlan", key: "avgCheckPlan", width: 140, render: (v, r) => (
                          <InputNumber min={0} step={10000} parser={stripLeadingZerosParser} value={r.avgCheckPlan} onFocus={(e) => { if (String(r.avgCheckPlan || 0) === '0') e.target.select(); }} onChange={(x) => setPlans((p) => ({ ...p, managers: p.managers.map(m => m.id === r.id ? { ...m, avgCheckPlan: Number(x || 0) } : m) }))} />
                        )
                      },
                    ]}
                  />
                  <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col>
                      <Statistic title="Всего — Лидов" value={planTotals.leads} />
                    </Col>
                    <Col>
                      <Statistic title="Всего — Квалифицированные" value={planTotals.qualified} />
                    </Col>
                    <Col>
                      <Statistic title="Всего — Замеры" value={planTotals.measure} />
                    </Col>
                    <Col>
                      <Statistic title="Всего — Число продаж" value={planTotals.salesCount} />
                    </Col>
                    <Col>
                      <Statistic title="Всего — Сумма продаж" value={planTotals.salesSum} formatter={(v) => formatCurrency(v, metrics.currency)} />
                    </Col>
                    <Col>
                      <Statistic
                        title="Итоговый план продаж (автоматически)"
                        value={planTotals.salesSum}
                        formatter={(v) => formatCurrency(v, metrics.currency)}
                        valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </TabPane>



          <TabPane tab="Статистика" key="stats">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card
                  title="Статистика по менеджерам (по датам)"
                  extra={
                    <CallsSyncManager
                      dateRange={{
                        startDate: getDateRangeFormatted().startISO.split('T')[0],
                        endDate: getDateRangeFormatted().endISO.split('T')[0]
                      }}
                      onSyncComplete={(result) => {
                        console.log('Синхронизация завершена:', result);
                        // Обновляем KPI данные
                        const { startISO, endISO } = getDateRangeFormatted();
                        loadKpi(startISO, endISO);
                      }}
                      onSyncError={(error) => {
                        console.error('Ошибка синхронизации:', error);
                      }}
                      compact={true}
                    />
                  }
                >
                  <Table
                    rowKey={(r) => r.id}
                    pagination={false}
                    bordered
                    dataSource={(kpi.managers || [])
                      // Показываем только менеджеров с планом (как в карточках)
                      .filter((km) => {
                        const normalizeName = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
                        const pr = plans.managers.find(x => x.id === km.id)
                          || plans.managers.find(x => normalizeName(x.name) === normalizeName(km.name));
                        if (!pr) return false;
                        const hasSalesPlan = Number(pr?.derived?.salesSumPlan || 0) > 0;
                        const hasInputPlans = Number(pr?.leadsPlan || 0) > 0 && Number(pr?.avgCheckPlan || 0) > 0;
                        return hasSalesPlan || hasInputPlans;
                      })
                      .map((km) => {
                        // Планы подбираем по id, иначе по имени
                        const normalizeName = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
                        const pm = plans.managers.find(x => x.id === km.id)
                          || plans.managers.find(x => normalizeName(x.name) === normalizeName(km.name))
                          || {};

                        // Применяем planMultiplier к планам менеджера
                        // Формула: (месячный_план / дни_в_месяце) * дни_в_периоде
                        const leadsMonthly = Number(pm.leadsPlan || 0);
                        const avg = Number(pm.avgCheckPlan || 0);
                        const baseLeads = Math.round(leadsMonthly * planMultiplier);

                        // Используем цели конверсий из вкладки «Планы» (а не константы)
                        const cv1 = Number(plans?.cvTargets?.cv1 || 0) / 100;
                        const cv2 = Number(plans?.cvTargets?.cv2 || 0) / 100;
                        const cv3 = Number(plans?.cvTargets?.cv3 || 0) / 100;
                        const qualified = Math.round(baseLeads * cv1);
                        const measure = Math.round(qualified * cv2);
                        const salesCount = Math.round(measure * cv3);
                        const kpiRow = km || {};
                        if (!kpiRow || (!kpiRow.salesSum && !kpiRow.leads && !kpiRow.salesCount)) {
                          console.log('⚠️ [Stats] no KPI row facts for', { id: km.id, name: km.name, kpiRow });
                        }
                        // Отладка: логируем если данные не найдены
                        if (!kpiRow.leads && !kpiRow.qualified && !kpiRow.measure && !kpiRow.salesCount) {
                          console.log(`No KPI data for manager ${km.name} (ID: ${km.id}). Available KPI managers:`, kpi.managers?.map(k => ({ id: k.id, name: k.name })));
                        }
                        const salesCountFact = Number(kpiRow.salesCount || 0);
                        const salesSumFact = (kpiRow.salesSum != null ? Number(kpiRow.salesSum) : salesCountFact * avg);
                        const avgCheckFact = salesCountFact ? Math.round(salesSumFact / salesCountFact) : 0;

                        // Планы сумм также применяем planMultiplier
                        const salesSumPlan = Math.round(Number(pm?.derived?.salesSumPlan || salesCount * avg) * planMultiplier);

                        return {
                          id: km.id,
                          name: km.name,
                          leadsPlan: baseLeads,
                          leadsFact: Number(kpiRow.leads || 0),
                          leadsManual: kpiRow.leadsManual || false,
                          callsIncomingFact: Number(kpiRow?.calls?.incomingMinutes ?? kpiRow.callsIncoming ?? 0),
                          callsOutgoingFact: Number(kpiRow?.calls?.outgoingMinutes ?? kpiRow.callsOutgoing ?? 0),
                          qualifiedPlan: qualified,
                          qualifiedFact: Number(kpiRow.qualified || 0),
                          qualifiedManual: kpiRow.qualifiedManual || false,
                          measurePlan: measure,
                          measureFact: Number(kpiRow.measure || 0),
                          measureManual: kpiRow.measureManual || false,
                          salesCountPlan: salesCount,
                          salesCountFact,
                          salesCountManual: kpiRow.salesCountManual || false,
                          salesSumPlan: salesSumPlan,
                          salesSumFact,
                          salesSumManual: kpiRow.salesSumManual || false,
                          avgCheckPlan: avg,
                          avgCheckFact,
                          avgCheckManual: kpiRow.avgCheckManual || false,
                        };
                      })}
                    columns={[
                      { title: 'Менеджер', dataIndex: 'name', key: 'name', fixed: 'left', width: 150 },
                      {
                        title: 'ЛИДЫ',
                        onHeaderCell: () => ({ className: 'rob-bg-leads' }),
                        children: [
                          { title: 'План', dataIndex: 'leadsPlan', key: 'leadsPlan', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-col-sep rob-bg-leads' }), onHeaderCell: () => ({ className: 'rob-bg-leads' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'leadsFact',
                            key: 'leadsFact',
                            render: (v, record) => (
                              <Space>
                                {formatNumber(v)}
                                {MANUAL_FLAGS_ENABLED && record.leadsManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-leads' + (MANUAL_FLAGS_ENABLED && record.leadsManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.leadsManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-leads' })
                          },
                        ],
                      },
                      {
                        title: 'ЗВОНКИ',
                        onHeaderCell: () => ({ className: 'rob-bg-calls' }),
                        children: [
                          { title: 'Входящие', dataIndex: 'callsIncomingFact', key: 'callsIncomingFact', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-col-sep rob-bg-calls' }), onHeaderCell: () => ({ className: 'rob-bg-calls' }) },
                          { title: 'Исходящие', dataIndex: 'callsOutgoingFact', key: 'callsOutgoingFact', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-bg-calls' }), onHeaderCell: () => ({ className: 'rob-bg-calls' }) },
                        ],
                      },
                      {
                        title: 'КВАЛ ЛИДЫ',
                        onHeaderCell: () => ({ className: 'rob-bg-qual' }),
                        children: [
                          { title: 'План', dataIndex: 'qualifiedPlan', key: 'qualifiedPlan', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-col-sep rob-bg-qual' }), onHeaderCell: () => ({ className: 'rob-bg-qual' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'qualifiedFact',
                            key: 'qualifiedFact',
                            render: (v, record) => (
                              <Space>
                                {formatNumber(v)}
                                {MANUAL_FLAGS_ENABLED && record.qualifiedManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-qual' + (MANUAL_FLAGS_ENABLED && record.qualifiedManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.qualifiedManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-qual' })
                          },
                        ],
                      },
                      {
                        title: 'ЗАМЕРЫ',
                        onHeaderCell: () => ({ className: 'rob-bg-measure' }),
                        children: [
                          { title: 'План', dataIndex: 'measurePlan', key: 'measurePlan', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-col-sep rob-bg-measure' }), onHeaderCell: () => ({ className: 'rob-bg-measure' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'measureFact',
                            key: 'measureFact',
                            render: (v, record) => (
                              <Space>
                                {formatNumber(v)}
                                {MANUAL_FLAGS_ENABLED && record.measureManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-measure' + (MANUAL_FLAGS_ENABLED && record.measureManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.measureManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-measure' })
                          },
                        ],
                      },
                      {
                        title: 'ПРОДАЖИ',
                        onHeaderCell: () => ({ className: 'rob-bg-sales' }),
                        children: [
                          { title: 'План', dataIndex: 'salesCountPlan', key: 'salesCountPlan', render: (v) => formatNumber(v), onCell: () => ({ className: 'rob-col-sep rob-bg-sales' }), onHeaderCell: () => ({ className: 'rob-bg-sales' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'salesCountFact',
                            key: 'salesCountFact',
                            render: (v, record) => (
                              <Space>
                                {formatNumber(v)}
                                {MANUAL_FLAGS_ENABLED && record.salesCountManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-sales' + (MANUAL_FLAGS_ENABLED && record.salesCountManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.salesCountManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-sales' })
                          },
                        ],
                      },
                      {
                        title: 'СУММА ПРОДАЖ',
                        onHeaderCell: () => ({ className: 'rob-bg-sum' }),
                        children: [
                          { title: 'План', dataIndex: 'salesSumPlan', key: 'salesSumPlan', render: (v) => formatCurrency(v || 0, metrics.currency), onCell: () => ({ className: 'rob-col-sep rob-bg-sum' }), onHeaderCell: () => ({ className: 'rob-bg-sum' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'salesSumFact',
                            key: 'salesSumFact',
                            render: (v, record) => (
                              <Space>
                                {formatCurrency(v || 0, metrics.currency)}
                                {MANUAL_FLAGS_ENABLED && record.salesSumManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-sum' + (MANUAL_FLAGS_ENABLED && record.salesSumManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.salesSumManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-sum' })
                          },
                        ],
                      },
                      {
                        title: 'СРЕДНИЙ ЧЕК',
                        onHeaderCell: () => ({ className: 'rob-bg-avg' }),
                        children: [
                          { title: 'План', dataIndex: 'avgCheckPlan', key: 'avgCheckPlan', render: (v) => formatCurrency(v || 0, metrics.currency), onCell: () => ({ className: 'rob-col-sep rob-bg-avg' }), onHeaderCell: () => ({ className: 'rob-bg-avg' }) },
                          {
                            title: 'Факт',
                            dataIndex: 'avgCheckFact',
                            key: 'avgCheckFact',
                            render: (v, record) => (
                              <Space>
                                {formatCurrency(v || 0, metrics.currency)}
                                {MANUAL_FLAGS_ENABLED && record.avgCheckManual && (
                                  <Tooltip title="Ручная корректировка">
                                    <Badge status="warning" />
                                  </Tooltip>
                                )}
                              </Space>
                            ),
                            onCell: (record) => ({
                              className: 'rob-bg-avg' + (MANUAL_FLAGS_ENABLED && record.avgCheckManual ? ' manual-fact-cell' : ''),
                              style: MANUAL_FLAGS_ENABLED && record.avgCheckManual ? { backgroundColor: '#fffbe6' } : {}
                            }),
                            onHeaderCell: () => ({ className: 'rob-bg-avg' })
                          },
                        ],
                      },
                      {
                        title: 'Действия',
                        key: 'actions',
                        fixed: 'right',
                        width: 120,
                        render: (_, record) => {
                          // Показываем кнопки только для администраторов
                          if (!isAdminView) {
                            return <span style={{ color: '#999' }}>Нет доступа</span>;
                          }

                          return (
                            <Space>
                              <Tooltip title="Редактировать факты">
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleOpenEditModal({ id: record.id, name: record.name })}
                                />
                              </Tooltip>
                              <Tooltip title="История изменений">
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<HistoryOutlined />}
                                  onClick={() => handleOpenHistoryModal({ id: record.id, name: record.name })}
                                />
                              </Tooltip>
                            </Space>
                          );
                        },
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Ежедневный отчет" key="daily">
            <DailyReport
              dateRange={dateRange}
              managers={managersDisplay}
              onSaveManualFact={handleSaveManualFacts}
              isAdmin={isAdminView}
              refreshKey={dailyRefresh}
            />
          </TabPane>

          <TabPane tab="Конверсия" key="conversion">
            <ConversionReport
              managers={managersDisplay}
              plans={plans}
              kpi={kpi}
              currency={metrics.currency}
              dateRange={dateRange}
            />
          </TabPane>

        </Tabs>
      )}
      {error && <Alert type="error" showIcon message={error} />}


      {/* Модальные окна для редактирования фактов */}
      {selectedManagerForEdit && (
        <>
          <EditFactsModal
            visible={editModalVisible}
            manager={selectedManagerForEdit}
            onSave={handleSaveManualFacts}
            onCancel={handleCloseEditModal}
            currentFacts={(kpi.managers || []).find(m => m.id === selectedManagerForEdit.id) || {}}
            currency={metrics.currency}
          />
          <FactsHistoryModal
            visible={historyModalVisible}
            manager={selectedManagerForEdit}
            onClose={handleCloseHistoryModal}
            fetchHistory={getFactsHistory}
            currency={metrics.currency}
          />
        </>
      )}

    </Space>
  );
};

export default KeplerRob;


